/**
 * Optimized Carousel Item with lazy loading, intersection observer, and memory optimization
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Paper, Box, Skeleton } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import theme from '../../../theme';
import { ReadBanner } from '../../../interfaces/core/ReadBanner';

const baseURL = process.env.REACT_APP_API_URL;

interface OptimizedCarouselItemProps {
  banner: ReadBanner;
  index: number;
  isCritical: boolean;
  height: string;
}

const OptimizedCarouselItem: React.FC<OptimizedCarouselItemProps> = React.memo(({
  banner,
  index,
  isCritical,
  height
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(isCritical); // Critical images start as "in view"
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Intersection Observer for lazy loading (skip for critical images)
  useEffect(() => {
    if (isCritical) return; // Don't lazy load critical images
    
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1, // Load when 10% visible
        rootMargin: '50px' // Start loading 50px before entering viewport
      }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, [isCritical]);
  
  // Handle image load success
  const handleImageLoad = useCallback(() => {
    setIsLoaded(true);
    setHasError(false);
  }, []);
  
  // Handle image load error
  const handleImageError = useCallback(() => {
    setHasError(true);
    setIsLoaded(false);
  }, []);
  
  // Generate optimized image URL
  const imageUrl = banner.image?.startsWith('http') 
    ? banner.image 
    : `${baseURL}/${banner.image}`;
  
  return (
    <Paper 
      ref={containerRef}
      sx={{ 
        width: '100%', 
        height: height,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
      }}
    >
      {/* Loading skeleton - shown until image loads */}
      {!isLoaded && !hasError && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: 'rgba(0,0,0,0.06)',
            zIndex: 1
          }}
        />
      )}
      
      {/* Error fallback */}
      {hasError && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f0f0',
            color: theme.palette.text.secondary
          }}
        >
          <ImageIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
          <span style={{ fontSize: '14px' }}>Image unavailable</span>
        </Box>
      )}
      
      {/* Actual image - only render when in view or critical */}
      {(isInView || isCritical) && !hasError && (
        <img
          ref={imgRef}
          src={imageUrl}
          alt={`Banner ${index + 1}`}
          loading={isCritical ? 'eager' : 'lazy'}
          fetchPriority={isCritical ? 'high' : 'auto'}
          draggable={false}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            // Hardware acceleration for better performance
            transform: 'translateZ(0)',
            willChange: 'opacity',
            // Prevent image selection
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
          // Add responsive sizing attributes for better performance
          sizes={`(max-width: ${theme.breakpoints.values.md}px) 100vw, 100vw`}
        />
      )}
    </Paper>
  );
});

OptimizedCarouselItem.displayName = 'OptimizedCarouselItem';

export default OptimizedCarouselItem;
